import { Component, OnInit, OnDestroy} from '@angular/core';
import * as _ from 'lodash';
import * as Chart from 'chart.js';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import PreferencesModel from 'src/app/services/preferences.model';
import { PreferencesService } from 'src/app/services/preferences.service';
import FeedLogModel from 'src/app/services/feedLog.model';
import { FeedLogService } from 'src/app/services/feedLog.service';
import PersonsModel from '../../../../../services/persons.model';
import {PersonsService} from '../../../../../services/persons.service';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component FeedComponent
 * @selector app-feed
 * @description
 * The `FeedComponent` is responsible for displaying Feed Conversion Ratio (FCR) metrics for farms.
 * It provides a dashboard view with visual charts (Doughnut or Bar) representing FCR distribution across ponds,
 * and a detailed list of feed logs.
 *
 * ## Key Responsibilities:
 * 1. **Data Aggregation**: Fetches farm sites and aggregates feed log data to calculate FCR summaries.
 * 2. **Visualization**: Renders FCR distribution using Chart.js. Supports toggling between Doughnut and Bar charts.
 * 3. **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (Farmer, Farm Manager, Business Admin, Provider).
 * 4. **User Preferences**: Loads and saves user preferences for chart types.
 * 5. **Filtering**: Provides client-side filtering for the feed log list.
 *
 * ## User Roles & Permissions:
 * - **Farmer / Business Admin (Farm)**: View data for their specific farm business.
 * - **Farm Manager**: View data for farms assigned to them.
 * - **Business Admin (Provider)**: View data for farms associated with the provider.
 * - **Provider Manager**: View data for farms assigned to their technicians.
 * - **Employee**: View data for all farm sites (typically internal admin).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessName`: Business name.
 * - `loginBusinessCategory`: Business category.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `fcrType`: Saved preference for chart type.
 *
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Determines user context from `localStorage`.
 *    - Fetches relevant farm sites.
 *    - Calls `getOnLoadCallsData` with farm site IDs.
 * 2. **Data Fetching (`getOnLoadCallsData`)**:
 *    - Fetches FCR summary (`getFCRSummary`) and details (`getFCRDetails`) concurrently.
 *    - Prepares chart data (`piekeys`, `piedata`).
 *    - Renders the chart (`prepareDoughnutFCRChart`).
 * 3. **Interaction**:
 *    - User can switch chart types via `graphselection`.
 *    - User can search the list via `searchBusiness`.
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-feed',
  templateUrl: './feed.component.html',
  styleUrls: ['./feed.component.scss']
})
export class FeedComponent implements OnInit, OnDestroy{
  /**
   * @type {Array<PreferencesModel>}
   * @description User preferences for chart display settings.
   */
  public preferences: Array<PreferencesModel>;

  /**
   * @type {Array<FeedLogModel>}
   * @description Summary of feed logs used for chart data aggregation.
   */
  public feedlogs: Array<FeedLogModel>;

  /**
   * @type {Array<FeedLogModel>}
   * @description List of feed logs grouped by location for FCR analysis.
   */
  public pondsByFCRLocations: Array<FeedLogModel>;

  /**
   * @type {Array<FeedLogModel>}
   * @description Detailed list of feed logs displayed in the table.
   */
  public fcrDetailsList: Array<FeedLogModel>;

  /**
   * @type {FeedLogModel}
   * @description Single feed log model instance.
   */
  public feedlog: FeedLogModel;

  /**
   * @type {Array<FarmSiteModel>}
   * @description List of farm sites associated with the user context.
   */
  public farmSites: Array<FarmSiteModel>;

  /**
   * @type {Array<PersonsModel>}
   * @description List of technicians (used for Provider Manager role).
   */
  public technicians: Array<PersonsModel>;

  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if the page data (charts/lists) has loaded.
   */
  public pgLoad = false;

  /**
   * @type {object}
   * @description Keys (labels) for the FCR chart.
   */
  public piekeys: object;

  /**
   * @type {object}
   * @description Data values for the FCR chart.
   */
  public piedata: object;

  /**
   * @type {string}
   * @description Title displayed on the chart.
   */
  public charttitle: string;

  /**
   * @type {Array<any>}
   * @description List of farm site IDs used for data fetching.
   */
  public farmSiteIds = new Array();

  /**
   * @type {Array<any>}
   * @description List of technician IDs (for Provider Manager).
   */
  public technicianIds = new Array();

  /**
   * @type {string}
   * @description Role of the logged-in user.
   */
  public loginPersonType: string;

  /**
   * @type {string}
   * @description Name of the logged-in user's business.
   */
  public loginUserBusinessName: string;

  /**
   * @type {string}
   * @description Category of the logged-in user's business.
   */
  public loginUserBusinessCategory: string;

  /**
   * @type {any}
   * @description Chart.js instance.
   */
  public graph: any;

  /**
   * @type {any}
   * @description Configuration object for the chart.
   */
  public graphconfig: any;

  /**
   * @type {string}
   * @description Current chart type (e.g., 'bar', 'doughnut').
   */
  public fcrType: string;

  /**
   * @type {string}
   * @description Search keyword for filtering the list.
   */
  public search: string;

  /**
   * @type {boolean}
   * @description Flag indicating if the initial response has loaded.
   */
  public responseLoad: boolean;

  /**
   * @constructor
   * @description Initializes the component and services.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {PersonsService} personService - Service for person-related operations.
   * @param {FeedLogService} feedLogService - Service for fetching feed log data.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {PreferencesService} preferencesService - Service for managing user preferences.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous routes.
   */
  constructor(public sidenavaction: Sidenavaction, public personService: PersonsService, public feedLogService: FeedLogService, public farmSiteService: FarmSiteService, public preferencesService: PreferencesService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.feedlog = new FeedLogModel();
  }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description
   * Lifecycle hook that initializes the component.
   * 1. Sets the previous route.
   * 2. Retrieves user context from localStorage.
   * 3. Determines the chart type preference.
   * 4. Fetches farm sites based on the user's role and business category.
   * 5. Calls `getOnLoadCallsData` to fetch metrics data.
   *
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.farmSiteIds = new Array();
      // loginperson type details
      if (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin') {
        this.fcrType = localStorage.getItem('fcrType');
        if (this.fcrType == '') {
          this.fcrType = 'bar';
        }
      }
      // get person list based on login businessid
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByFarmsId(localStorage.businessID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByBAdminId(localStorage.businessID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else if (this.loginPersonType == 'Provider Manager') {
        this.technicians = await this.personService.getAllTechniciansByProviderManagerId(localStorage.personID);
        this.technicianIds = this.technicians.map(technician => technician.id);
        this.farmSites = await this.farmSiteService.getFarmeSitesByTechnicianIDs(this.technicianIds);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else {
        this.farmSites = await this.farmSiteService.getAllFarmSites();
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      }
      this.responseLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description
   * Lifecycle hook that cleans up resources when the component is destroyed.
   * Cancels ongoing HTTP requests for FarmSite, FeedLog, and Preferences services.
   *
   * @returns {void}
   */
public ngOnDestroy(): void {
  this.farmSiteService.cancelOnGoingHttpRequests();
  this.feedLogService.cancelOnGoingHttpRequests();
  this.preferencesService.cancelOnGoingHttpRequests();
}

  /**
   * @method getOnLoadCallsData
   * @async
   * @description
   * Fetches FCR summary and details data for the provided farm sites.
   * 1. Calls `getFCRSummary` and `getFCRDetails` concurrently.
   * 2. Processes summary data for the chart.
   * 3. Loads user preferences for chart type if applicable.
   * 4. Renders the chart.
   *
   * @param {Array<FarmSiteModel>} farmSiteIds - List of farm site IDs to fetch data for.
   * @returns {Promise<void>}
   */
  public async getOnLoadCallsData(farmSiteIds: Array<FarmSiteModel>) {
    try {
      let promiseResults = new Array();
      await Promise.all([
        await this.feedLogService.getFCRSummary(farmSiteIds),
        await this.feedLogService.getFCRDetails(farmSiteIds)
      ]).then((resolve) => {
        promiseResults = resolve;
      }).catch((reject) => {
        alert(reject);
      });
      this.feedlogs = promiseResults[0];
      if (this.feedlogs !== undefined) {
        this.feedlogs = Object.assign({}, this.feedlogs);
        delete this.feedlogs['pondsCount'];
        this.piekeys = Object.keys(this.feedlogs);
        this.piedata = Object.values(this.feedlogs);
        this.pgLoad = true;
        if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
          let preferences = await this.preferencesService.getPeferencesbyUserIDBusinessID(localStorage.businessID, localStorage.userID);
          if (preferences.length > 0) {
            this.fcrType = preferences[0].chartType;
          } else {
            this.fcrType = 'doughnut';
          }
          this.prepareDoughnutFCRChart(this.fcrType);
        } else {
          let feedcharttype = this.fcrType || 'doughnut';
          this.prepareDoughnutFCRChart(feedcharttype);
        }
      }
      this.fcrDetailsList = promiseResults[1];
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareDoughnutFCRChart
   * @description
   * Configures and renders the FCR chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `type` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} type - The type of chart to render ('doughnut' or 'bar').
   * @returns {void}
   */
  public prepareDoughnutFCRChart(type: string) {
    this.charttitle = 'Ponds by FCR' + ' ' + ' ' + ' ';
    if (this.feedlogs['pondsCount']) {
      this.charttitle += this.feedlogs['pondsCount'] + ' ponds';
    }
    let ctx: any = document.getElementById('myChart');
    let ctx1: any = ctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.piedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let fcrData = false;
    if (isallareZeros.length == 0) {
      fcrData = true;
    }
    let legenddisply = true;
    if (type == 'bar') {
      legenddisply = false;
    }
    this.graphconfig = {
      data: {
        labels: this.piekeys,
        datasets: [{
          backgroundColor: [
            '#A9A9A9',
            '#2ecc71',
            '#9b59b6',
            '#FAFF75',
            '#95a5a6',
            '#F0785F'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.piedata
        }]
      },
      options: {
        legend: {
          // display: fcrData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: fcrData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.graphconfig);
    temp.type = type || 'doughnut';
    // FOR NO DATA FOUND
    this.graph = new Chart(ctx1, temp);
  }

  /**
   * @method graphselection
   * @description
   * Handles the user's selection of chart type (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} type - The selected chart type.
   * @returns {void}
   */
  public graphselection(type: string) {
    // if (this.doughnutchecked == 'doughnut') {
    if (type == 'bar') {
      let ele = '<canvas id=\'myChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('myChart');
      let ctx1: any = ctx.getContext('2d');

      if (this.graph) {
        this.graph.destroy();
      }
      this.graphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.graphconfig);
      temp.type = 'bar';
      this.graph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('fcrType', temp.type);
      }
    } else if (type == 'doughnut') {
      let ele = '<canvas id=\'myChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('myChart');
      let ctx1: any = ctx.getContext('2d');
      if (this.graph) {
        this.graph.destroy();
      }
      this.graphconfig.options.legend.display = true;
      let temp = $.extend(true, {}, this.graphconfig);
      temp.type = 'doughnut';
      this.graph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('fcrType', temp.type);
      }
    }
  }

  /**
   * @method getAllPondsByFCRLocation
   * @async
   * @description
   * Fetches FCR location summary data.
   * Populates `pondsByFCRLocations`.
   *
   * @returns {Promise<void>}
   */
  public async getAllPondsByFCRLocation() {
    try {
      this.pondsByFCRLocations = await this.feedLogService.getFCRLocationSummary(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method charttypesavetoDB
   * @async
   * @description
   * Saves the user's chart type preference to the database via `PreferencesService`.
   *
   * @param {string} type - The chart type to save.
   * @returns {Promise<void>}
   */
  public async charttypesavetoDB(type: string) {
    try {
      this.preferences = await this.preferencesService.addPreferences(localStorage.businessID, localStorage.userID, type);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method searchBusiness
   * @description
   * Filters the displayed list based on the `search` string.
   * Uses jQuery to toggle visibility of elements with class `searchfun`.
   *
   * @returns {void}
   */
  public searchBusiness() {
    let value = this.search.toLowerCase();
    $('.searchfun').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }
}
